import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ICentroMonitoreo, TipoDelegacion } from 'modelosSeguridad/src';
import { MaterialModule } from '../../../../auxiliares/material.module';
import { IntegracionesService } from '../../../../auxiliares/servicios/http/integraciones.service';
import { InfoBannerComponent } from '../../../../componentes/info-banner/info-banner.component';
import { CardHeaderComponent } from '../../../../componentes/card-header/card-header.component';
import { NgSelectModule } from '@ng-select/ng-select';

interface DialogData {}

export interface DerivarAlertaDialogResult {
  tipo: TipoDelegacion;
  idCentroMonitoreo?: string;
  entidadExterna?: string;
}

@Component({
  selector: 'app-derivar-alerta-dialog',
  templateUrl: './derivar-alerta-dialog.component.html',
  styleUrl: './derivar-alerta-dialog.component.scss',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MaterialModule,
    InfoBannerComponent,
    CardHeaderComponent,
    NgSelectModule,
  ],
})
export class DerivarAlertaDialogComponent implements OnInit {
  data = inject<DialogData>(MAT_DIALOG_DATA);
  private dialogRef =
    inject<
      MatDialogRef<
        DerivarAlertaDialogComponent,
        DerivarAlertaDialogResult | null
      >
    >(MatDialogRef);
  private service = inject(IntegracionesService);

  public form = new FormGroup({
    tipo: new FormControl<TipoDelegacion>('normal', Validators.required),
    idCentroMonitoreo: new FormControl<string | null>(null),
    entidadExterna: new FormControl<string | null>(null),
  });

  /** Opciones del tipo de derivación (antes fijas en la plantilla) */
  public readonly tiposDerivacion: {
    valor: TipoDelegacion;
    etiqueta: string;
  }[] = [
    { valor: 'normal', etiqueta: 'Normal (la alerta pasa al otro centro)' },
    {
      valor: 'visibilidad-compartida',
      etiqueta: 'Visibilidad Compartida (ambos centros la ven)',
    },
    {
      valor: 'externa',
      etiqueta: 'Derivación Externa (entidad fuera del sistema)',
    },
  ];

  public centrosMonitoreo: ICentroMonitoreo[] = [];
  public entidadesExternas: string[] = [];
  public entidadesExternasFiltradas: string[] = [];

  async ngOnInit(): Promise<void> {
    this.form.get('tipo')?.valueChanges.subscribe((tipo) => {
      const centro = this.form.get('idCentroMonitoreo');
      const externa = this.form.get('entidadExterna');
      centro?.clearValidators();
      externa?.clearValidators();
      if (tipo === 'externa') {
        externa?.setValidators([Validators.required]);
        if (!this.entidadesExternas.length) void this.cargarEntidadesExternas();
      } else {
        centro?.setValidators([Validators.required]);
      }
      centro?.updateValueAndValidity();
      externa?.updateValueAndValidity();
    });

    this.form.get('entidadExterna')?.valueChanges.subscribe((value) => {
      this.filtrarEntidadesExternas(value || '');
    });

    this.form.get('idCentroMonitoreo')?.setValidators([Validators.required]);
    this.form.get('idCentroMonitoreo')?.updateValueAndValidity();

    await this.cargarCentrosMonitoreo();
  }

  private async cargarCentrosMonitoreo(): Promise<void> {
    try {
      const res = await this.service.getCentrosMonitoreo({
        filter: JSON.stringify({ tipo: 'Derivado' }),
        limit: 500,
      });
      this.centrosMonitoreo = res?.datos || [];
    } catch (error) {
      console.error('Error cargando centros monitoreo', error);
    }
  }

  private async cargarEntidadesExternas(): Promise<void> {
    try {
      this.entidadesExternas = await this.service.getEntidadesExternas();
      this.entidadesExternas.sort((a, b) => a.localeCompare(b));
      this.entidadesExternasFiltradas = [...this.entidadesExternas];
    } catch (error) {
      console.error('Error cargando entidades externas', error);
    }
  }

  private filtrarEntidadesExternas(valor: string): void {
    if (!valor || !valor.trim()) {
      this.entidadesExternasFiltradas = [...this.entidadesExternas];
      return;
    }
    const filtro = valor.toLowerCase().trim();
    this.entidadesExternasFiltradas = this.entidadesExternas.filter((e) =>
      e.toLowerCase().includes(filtro),
    );
  }

  public displayEntidadExterna(value: string): string {
    return value || '';
  }

  public confirmar(): void {
    if (this.form.invalid) return;
    const tipo = this.form.value.tipo as TipoDelegacion;
    this.dialogRef.close({
      tipo,
      idCentroMonitoreo:
        tipo !== 'externa'
          ? this.form.value.idCentroMonitoreo || undefined
          : undefined,
      entidadExterna:
        tipo === 'externa'
          ? this.form.value.entidadExterna || undefined
          : undefined,
    });
  }

  public cancelar(): void {
    this.dialogRef.close(null);
  }
}
